import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import type { Schema } from '@/api/client'
import type { Query } from '@/api/query'
import { SCOPE_ANALYTICS, reads } from './ga4-state'
import { useBrand } from '@/brand/context'
import { Ga4Picker } from '@/settings/google-properties/Ga4Picker'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Google Analytics 4 — the fourth card in the Connections panel, audit row §G28.
 *
 * WHY THIS IS NOT THE CARD THE MOCKUP DRAWS
 * -----------------------------------------
 * `design/screens/shell.html:402` draws GA4 as a fifth connection beside Search
 * Console and LinkedIn, with its own **Connect** button. There is no such
 * connection. One Google OAuth grant carries all three scopes — `SCOPES` in
 * `api/app/seo/oauth.py:116` is `(webmasters.readonly, siteverification,
 * analytics.readonly)` — and the card above this one already grants it. A second
 * Connect button would either start the same flow under a different name, or
 * promise a GA4-only grant the API cannot issue. Both are the "control that
 * lies" this panel exists to remove, so **this card has no Connect button.**
 * What it does have, since the settings consolidation's second batch
 * (2026-09-15), is the per-site choice itself: *Change* on a site's line opens
 * the GA4 picker under it, which used to live on Search performance.
 *
 * WHY IT MAKES A SECOND FETCH
 * ---------------------------
 * *"GA4 is connected"* is a conjunction of three facts, and no single route
 * carries them. `GET /connections/google` answers whether the grant is live and
 * which scopes it holds; `ga4_property_id` is deliberately **not** on that
 * response's `SeoSiteOut`, so which site reads which property comes from
 * `GET /seo/sites`. Both are `tenant:read`, the same permission that opens this
 * panel, so the second read cannot 403 for anybody who can see the card.
 *
 * WHY `connected` IS TESTED AS WELL AS THE SCOPE AND THE PROPERTY
 * --------------------------------------------------------------
 * Because the other two survive a dead grant. `mark_no_access`
 * (`api/app/db/seo.py:276`) writes `status` and `last_error` and leaves `detail`
 * alone, so `scopes` outlives a revocation; `mark_pending` (`:161`) does the same
 * on a re-connect that was abandoned at the consent screen. Neither calls
 * `clear_properties`, so `ga4_property_id` outlives both. A two-fact test would
 * therefore paint a green tick on a connection Google has stopped honouring —
 * the exact failure `searchconsole.test.tsx` guards its own card against.
 * Only Disconnect clears the residue, and `clear_properties` (`:417`) nulls
 * `ga4_property_id` alongside `gsc_property` because one grant backs both.
 *
 * The three states every component ships (CLAUDE.md, Frontend): loading while
 * the two reads run, an error state, and an empty state written as an invitation.
 * **The error state carries no retry**, and that is deliberate rather than an
 * omission: the connection it failed to read is the one the card above owns, and
 * that card's retry re-runs the very query this one is waiting on. Two *Try
 * again* buttons for one endpoint is a worse screen than one.
 */

type GoogleConnection = Schema<'SearchConsoleConnectionOut'>

export function Ga4Card({
  connection,
  sites,
}: {
  connection: Query<GoogleConnection>
  sites: Query<Schema<'SiteListResponse'>>
}): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const { membership } = useActiveTenant()
  // `discovery:manage` is what `PUT /seo/sites/{id}/analytics-property` is gated on.
  const editable = may(membership, 'discovery:manage')
  const [picking, setPicking] = useState<string | null>(null)
  // Both reads are the panel's, shared with the rows above: the connection
  // with the Search Console card, so a disconnect there cannot leave this card
  // claiming a grant that has just been deleted; the site list with the GA4
  // row, so the row and the card cannot disagree about whether a property is
  // chosen.
  const data = connection.state.data
  const listed = sites.state.data?.sites ?? []
  const connected = data?.connected === true
  const granted = data?.scopes?.includes(SCOPE_ANALYTICS) === true
  const reading = listed.filter(reads)
  const failure = connection.state.failure ?? sites.state.failure
  // The badge waits for BOTH reads, not just the connection's. The property
  // count comes from the site list, so a badge drawn as soon as the connection
  // landed would say *No property chosen* to every tenant for as long as the
  // second request took, and then flip to *Reading traffic*. A status that is
  // briefly wrong is worse than one that is briefly absent.
  const settled = connection.state.status === 'ready' && sites.state.status === 'ready'

  return (
    <section className="ar-card" id="analytics-properties">
      <div className="ar-card__header">
        <h2>Google Analytics 4</h2>
        <span className="ar-card__spacer" />
        {/*
          Short wordings, and measured rather than guessed. `.ar-badge` is
          `white-space: nowrap`, so a long badge does not truncate — it pushes
          the header past its width and `flex-wrap` drops it to a second line.
          *Analytics permission not granted* did that below 504px against the
          Search Console card's 472px, so this card degraded 32px earlier than
          the one above it for no gain: both words are unambiguous inside a card
          headed *Google Analytics 4*.
        */}
        {!settled ? null : connected && granted && reading.length > 0 ? (
          <span className="ar-badge ar-badge--success">✓ Reading traffic</span>
        ) : connected && granted ? (
          <span className="ar-badge ar-badge--warning">⚠ No property chosen</span>
        ) : connected ? (
          <span className="ar-badge ar-badge--warning">⚠ Permission not granted</span>
        ) : null}
      </div>

      <div className="ar-card__body">
        <p className="ar-text-body-sm">
          Sessions, engaged sessions and key events, shown beside your search figures so a page that
          gained impressions can be checked against whether it gained visits.{' '}
          <strong>This is not a separate connection.</strong> Analytics rides on the one Google
          authorisation in the Connections list above — there is nothing extra to sign in to here.
        </p>

        {connection.state.status === 'loading' || sites.state.status === 'loading' ? (
          <Loading label="Loading your Analytics setup" />
        ) : failure !== null ? (
          <>
            <ErrorState failure={failure} title="We could not read your Analytics setup" />
            {/*
              Which read failed decides which recovery is true. The connection is
              the panel's, so the card above is showing the same failure with a
              working *Try again* beside it. The site list is this card's alone —
              nothing else on the screen refetches it, the card above is not even
              in an error state, and pointing at a button that is not rendered is
              worse than admitting a reload is the fix.
            */}
            <p className="ar-text-body-sm ar-muted">
              {connection.state.failure !== null
                ? 'The Connections list above reads the same connection — press Try again there and this card recovers with it.'
                : 'Your Google connection is fine; it is the list of your sites that could not be read. Reload the page to try again.'}
            </p>
          </>
        ) : data === null ? (
          <p className="ar-empty ar-empty--inline">
            We could not read your Analytics setup just now. Reload the page to try again.
          </p>
        ) : !connected ? (
          /*
            Two sentences, because *never connected* and *connected and then
            withdrawn* are not the same news. `status === null` is the only value
            meaning nobody has ever pressed Connect; every other value means the
            card above is already saying something specific — *Access withdrawn*
            on `no_access` — and first-run copy underneath it reads as though the
            product had forgotten.
          */
          <p className="ar-empty ar-empty--inline">
            {data.status === null
              ? `Google is not connected, so there are no traffic figures anywhere in ${brand.productName} yet. Connect Google in the list above, and leave the Analytics permission ticked when Google asks.`
              : `${brand.productName} can no longer read your Analytics, because the Google connection behind it has stopped working. Reconnect in the list above — that restores both halves at once.`}{' '}
            Connecting is the first of two steps: each site then needs an Analytics property chosen
            for it, on the lines that appear here.
          </p>
        ) : !granted ? (
          <p className="ar-callout ar-text-body-sm" data-tone="warning">
            This Google connection was not given permission to read Analytics. Google shows one tick
            box per permission and this one was cleared, so {brand.productName} can read how your
            pages rank but not whether anybody visited them. Press <strong>Connect</strong> in the
            Connections list above and accept the Analytics permission — Google is asked to show the
            tick boxes again every time, so they will be there.
          </p>
        ) : listed.length === 0 ? (
          <p className="ar-empty ar-empty--inline">
            {brand.productName} may read your Analytics, but you have no sites yet, so there is
            nothing to point at a property. <Link to="/onboarding/site">Add your site</Link> and the
            choice appears beside its search figures. (The Sites screen lists them and does not add
            them — the only writer of a site is the onboarding step, which is why every empty state
            in the product points there.)
          </p>
        ) : (
          <>
            <ul
              className="ar-text-body-sm ar-stack ar-stack--tight"
              style={{ margin: 0, paddingLeft: 'var(--ar-space-5)' }}
            >
              {listed.map((site) => (
                <li key={site.id}>
                  <strong>{site.domain}</strong>{' '}
                  {reads(site) ? (
                    <span className="ar-mono">{site.ga4_property_id}</span>
                  ) : (
                    <span className="ar-muted">— reading no Analytics property.</span>
                  )}{' '}
                  {editable && picking !== site.id ? (
                    <Button
                      size="sm"
                      tone="ghost"
                      aria-label={`Change the Analytics property for ${site.domain}`}
                      onClick={() => {
                        setPicking(site.id)
                      }}
                    >
                      {reads(site) ? 'Change' : 'Choose'}
                    </Button>
                  ) : null}
                  {picking === site.id ? (
                    <div
                      className="ar-stack ar-stack--tight"
                      style={{ marginTop: 'var(--ar-space-2)' }}
                    >
                      <Ga4Picker
                        siteId={site.id}
                        domain={site.domain}
                        current={site.ga4_property_id ?? null}
                        onChosen={() => {
                          setPicking(null)
                          sites.refetch()
                          toast.show({
                            kind: 'success',
                            title: 'Analytics property updated',
                            body: `${site.domain} now reads traffic from the property you chose.`,
                          })
                        }}
                      />
                      <div>
                        <Button
                          size="sm"
                          tone="quiet"
                          onClick={() => {
                            setPicking(null)
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
            <p className="ar-text-body-sm ar-muted">
              A property is chosen per site, because an agency running two domains needs two
              answers. The number shown is Analytics&rsquo; own property id, not the{' '}
              <span className="ar-mono">G-XXXXXXXXXX</span> measurement id in your tracking snippet
              — Google publishes both and they are not convertible.
            </p>
          </>
        )}

        {/*
          Only when Analytics is actually being read. Telling somebody who
          declined the permission that they cannot switch it off on its own
          describes a control they have no use for, about a thing that is
          already off — and it sits directly under a callout asking them to turn
          it ON, which is the opposite instruction.
        */}
        {settled && connected && granted ? (
          <p className="ar-text-body-sm ar-muted">
            There is no way to switch Analytics off on its own. <strong>Disconnect</strong> in the
            list above ends both at once — it deletes the stored Google credential and clears every
            site&rsquo;s Search Console <em>and</em> Analytics property.
          </p>
        ) : null}
      </div>
    </section>
  )
}
